"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Disc, Pause, Play, Trash2 } from "lucide-react";

import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { LiveWaveform } from "./ui/live-waveform";
import { MicSelector } from "./ui/mic-selector";
import { Separator } from "./ui/separator";
import { AudioRecorderComponentProps } from "../types";

type RecordingState = "idle" | "loading" | "recording" | "recorded" | "playing";

export function AudioRecorderComponent({
  onAudioRecorded,
  maxRecordingDuration = 90,
}: AudioRecorderComponentProps) {
  const [selectedDevice, setSelectedDevice] = useState<string>("");
  const [isMuted, setIsMuted] = useState(false);
  const [state, setState] = useState<RecordingState>("idle");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);

  const startRecording = useCallback(async () => {
    try {
      setState("loading");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: selectedDevice ? { deviceId: { exact: selectedDevice } } : true,
      });

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        onAudioRecorded?.(blob);
        stream.getTracks().forEach((track) => track.stop());
        setState("recorded");
      };

      mediaRecorder.start();
      setState("recording");
      setRecordingTime(0);

      // Start timer for configurable duration limit
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          console.log(`Timer tick: ${newTime}/${maxRecordingDuration}`);
          if (newTime >= maxRecordingDuration) {
            console.log("Auto-stopping recording due to time limit");
            // Auto-stop recording at configured duration
            if (mediaRecorderRef.current) {
              mediaRecorderRef.current.stop();
            }
            // Clear the timer
            if (recordingTimerRef.current) {
              clearInterval(recordingTimerRef.current);
              recordingTimerRef.current = null;
            }
            return maxRecordingDuration;
          }
          return newTime;
        });
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      setState("idle");
    }
  }, [selectedDevice, maxRecordingDuration, onAudioRecorded]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state === "recording") {
      mediaRecorderRef.current.stop();
    }

    // Clear the recording timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, [state]);

  const playRecording = useCallback(() => {
    if (!audioBlob) return;

    const audio = new Audio(URL.createObjectURL(audioBlob));
    audioElementRef.current = audio;

    audio.onended = () => {
      setState("recorded");
    };

    audio.play();
    setState("playing");
  }, [audioBlob]);

  const pausePlayback = useCallback(() => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      setState("recorded");
    }
  }, []);

  const restart = useCallback(() => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }

    // Clear the recording timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    setAudioBlob(null);
    onAudioRecorded?.(null);
    audioChunksRef.current = [];
    setRecordingTime(0);
    setState("idle");
  }, [onAudioRecorded]);

  // Stop recording when muted
  useEffect(() => {
    if (isMuted && state === "recording") {
      stopRecording();
    }
  }, [isMuted, state, stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
      if (audioElementRef.current) {
        audioElementRef.current.pause();
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  const showWaveform = state === "recording" && !isMuted;
  const showProcessing = state === "loading" || state === "playing";
  const showRecorded = state === "recorded";

  return (
    <div
      className="flex w-full items-center justify-center"
      role="region"
      aria-label="Audio recording controls"
    >
      <Card className="m-0 w-full max-w-2xl border p-0  gap-1 rounded-md">
        <div className="h-8  md:h-10  w-full my-1 px-1">
          <div
            className={cn(
              "flex h-full items-center gap-2 rounded-sm",
              "bg-foreground/5 text-foreground/70"
            )}
          >
            <div className="h-full flex-1">
              <div className="relative flex h-full w-full shrink-0 items-center justify-center overflow-hidden rounded-sm">
                <LiveWaveform
                  key={state}
                  active={showWaveform}
                  processing={showProcessing}
                  deviceId={selectedDevice}
                  barWidth={3}
                  barGap={1}
                  barRadius={4}
                  fadeEdges={true}
                  fadeWidth={24}
                  sensitivity={1.8}
                  smoothingTimeConstant={0.85}
                  height={20}
                  mode="static"
                  className={cn(
                    "h-full w-full transition-opacity duration-300",
                    state === "idle" && "opacity-0"
                  )}
                />
                {state === "idle" && (
                  <button
                    type="button"
                    className="absolute inset-0 flex items-center justify-center hover:bg-foreground/10 cursor-pointer disabled:opacity-50 disabled:cursor-default disabled:hover:bg-transparent"
                    onClick={startRecording}
                    disabled={isMuted}
                    aria-label="Start recording audio feedback"
                  >
                    <span className="text-foreground/50 text-[10px] font-medium">
                      Start Recording
                    </span>
                  </button>
                )}
                {state === "recording" && (
                  <div className="absolute top-0 right-2 flex items-center justify-center">
                    <span className="text-foreground/50 text-[10px] font-medium">
                      {maxRecordingDuration - recordingTime}s
                    </span>
                  </div>
                )}
                {showRecorded && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-foreground/50 text-[10px] font-medium">
                      Ready to Play
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 p-2">
          <div className="flex items-center justify-between w-full">
            <MicSelector
              value={selectedDevice}
              onValueChange={setSelectedDevice}
              muted={isMuted}
              onMutedChange={setIsMuted}
              disabled={state === "recording" || state === "loading"}
            />
            <Separator orientation="vertical" className="mx-1 -my-2.5" />
            <div className="flex items-center">
              {state === "idle" && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={startRecording}
                  disabled={isMuted}
                  aria-label="Start recording"
                >
                  <Disc className="h-5 w-5" />
                </Button>
              )}
              {(state === "loading" || state === "recording") && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={stopRecording}
                  disabled={state === "loading"}
                  aria-label="Stop recording"
                >
                  <Pause className="h-5 w-5" />
                </Button>
              )}
              {showRecorded && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={playRecording}
                  aria-label="Play recording"
                >
                  <Play className="h-5 w-5" />
                </Button>
              )}
              {state === "playing" && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={pausePlayback}
                  aria-label="Pause playback"
                >
                  <Pause className="h-5 w-5" />
                </Button>
              )}
              <Separator orientation="vertical" className="mx-1 -my-2.5" />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={restart}
                disabled={
                  state === "idle" ||
                  state === "loading" ||
                  state === "recording"
                }
                aria-label="Delete recording"
              >
                <Trash2 className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
